import { Injectable, inject } from '@angular/core';
import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { IMSVCErrors } from '@smsamiinc/catalog';
import { WarehouseService } from '@smsamiinc/inventory';
import { of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { SettingBaseModel } from 'src/app/models/settings-base.model';

import { TaxProfileModel } from '../../models/tax-profile';

import { OrgAction } from './org.action';
import { OrgService } from './org.service';
import { OrgStateModel } from './org.state.model';

@State<OrgStateModel>({
    name: 'org',
    defaults: new OrgStateModel(),
})
@Injectable()
export class OrgState {
    constructor(
        private orgService: OrgService,
        private store: Store
        // Note: WarehouseService removed from constructor - now lazily injected in UpdateWareHouse action
        // This prevents unnecessary dependency during auth module initialization
    ) {}

    @Selector()
    static org(state: OrgStateModel) {
        return state?.org;
    }
    @Selector()
    static taxes(state: OrgStateModel) {
        return (
            state?.org?.taxes?.map((tax) => {
                return { name: tax.name, taxPercent: +tax.rate };
            }) || []
        );
    }

    @Selector()
    static orgError(state: OrgStateModel) {
        return state?.orgError;
    }

    @Selector()
    static isDiscountEnable(state: OrgStateModel) {
        return state?.org?.IsDiscountSettingBase;
    }
    @Selector()
    static isReceiptNoteEnable(state: OrgStateModel) {
        return state?.org?.IsReceiptNoteSettingBase;
    }
    @Selector()
    static isReceiptNote(state: OrgStateModel) {
        return state.receiptOrderTypeNote;
    }

    @Selector()
    static updateProfileFormError(state: OrgStateModel) {
        return state.updateProfileForm.errors;
    }
    @Selector()
    static taxProfileListing(state: OrgStateModel) {
        return state.taxProfileListing;
    }
    @Selector()
    static taxProfileErrors(state: OrgStateModel) {
        return state.taxProfileErrors;
    }
    @Selector()
    static getTimeZone(state: OrgStateModel) {
        return state.time_zone;
    }
    @Action(OrgAction.Self)
    self(ctx: StateContext<OrgStateModel>) {
        return this.orgService.self().pipe(
            tap((resp) => {
                const timezone = resp.time_zone.display.split(' - ')[1];
                const unit = resp.settings.find((item: SettingBaseModel) => item?.unit === 'note');
                ctx.patchState({
                    org: resp,
                    receiptOrderTypeNote: unit?.data,
                    orgError: null,
                    time_zone: timezone,
                });
            }),
            catchError((err) => {
                ctx.patchState({
                    orgError: err,
                    org: null,
                });
                throw err;
            })
        );
    }

    @Action(OrgAction.UpdateProfile)
    updateProfile(ctx: StateContext<OrgStateModel>, action: OrgAction.UpdateProfile) {
        const updateProfileForm = action.payload;
        return this.orgService.updateProfile(updateProfileForm, this.organizationId()).pipe(
            tap((resp) => {
                ctx.patchState({
                    org: resp,
                });
            }),
            catchError((err) => {
                const state = ctx.getState();
                ctx.setState({
                    ...state,
                    updateProfileForm: {
                        ...state.updateProfileForm,
                        errors: err,
                    },
                });
                throw err;
            })
        );
    }

    @Action(OrgAction.SetOrgTime)
    setOrgTime(ctx: StateContext<OrgStateModel>, action: OrgAction.SetOrgTime) {
        return this.orgService.setOrgTime(action.payload, this.organizationId()).pipe(
            catchError((err) => {
                throw err;
            })
        );
    }
    @Action(OrgAction.SetReceiptNote)
    setReceiptNote(ctx: StateContext<OrgStateModel>, action: OrgAction.SetReceiptNote) {
        return this.orgService.setReceiptNote(action.payload, this.organizationId()).pipe(
            tap(() => {
                ctx.getState().receiptOrderTypeNote;
                ctx.patchState({
                    receiptOrderTypeNote: action.payload.data,
                });
            })
        );
    }
    @Action(OrgAction.UpdateWareHouse)
    updateWarehouse(ctx: StateContext<OrgStateModel>, action: OrgAction.UpdateWareHouse) {
        // Lazy inject WarehouseService only when this action is dispatched
        // This prevents forcing auth module to depend on inventory module
        const warehouseService = inject(WarehouseService);
        return warehouseService.createWarehouse(this.organizationId(), action.payload).pipe(
            catchError((err) => {
                throw err;
            })
        );
    }

    organizationId() {
        return this.store.selectSnapshot(OrgState.org).id;
    }

    @Action(OrgAction.GetAllTaxProfile)
    GetAllTaxProfile(ctx: StateContext<OrgStateModel>, action: OrgAction.GetAllTaxProfile) {
        const { id: organizationId } = action.payload;
        return this.orgService.GetAllTaxProfile(organizationId).pipe(
            //TODO : Will Update this any After Disscussion with Team in the next PR
            tap((resp: any) => {
                ctx.patchState({
                    taxProfileListing: resp?.taxProfile,
                });
            })
        );
    }

    @Action(OrgAction.AddTaxProfile)
    addTaxProfile(ctx: StateContext<OrgStateModel>, action: OrgAction.AddTaxProfile) {
        const { organizationId, profileData } = action.payload;
        return this.orgService.addTaxProfile(organizationId, profileData).pipe(
            //TODO : Will Update this any After Disscussion with Team in the next PR of Delete
            tap((resp: any) => {
                const updatedtaxProfileListing = resp?.taxProfile;
                ctx.patchState({
                    taxProfileListing: [...updatedtaxProfileListing],
                });
            }),
            catchError((errorResp) => {
                ctx.patchState({
                    taxProfileErrors: this.checkErrors(errorResp?.error),
                });
                throw errorResp;
            })
        );
    }

    @Action(OrgAction.updateTaxProfile)
    updateTaxProfile(ctx: StateContext<OrgStateModel>, action: OrgAction.updateTaxProfile) {
        const { organizationId, profileData } = action.payload;
        return this.orgService.updateTaxProfile(organizationId, profileData).pipe(
            //TODO : Will Update this any After Disscussion with backend Team
            tap((resp: any) => {
                const updatedtaxProfileListing = resp?.taxProfile;
                ctx.patchState({
                    taxProfileListing: updatedtaxProfileListing,
                });
            })
        );
    }

    @Action(OrgAction.deleteTaxProfile)
    deleteTaxProfile(ctx: StateContext<OrgStateModel>, action: OrgAction.deleteTaxProfile) {
        const { organizationId, taxId } = action.payload;
        return this.orgService.deleteTaxProfile(organizationId, taxId).pipe(
            //TODO : Will Update this any After Disscussion with Back end Team
            tap((resp: any) => {
                const updatedtaxProfileListing = resp?.taxInfo?.taxProfile;
                ctx.patchState({
                    taxProfileListing: [...updatedtaxProfileListing],
                });
            })
        );
    }
    private checkErrors(errorResp): IMSVCErrors {
        if (errorResp?.name === 'NotPermittedError') {
            return { name: 'Profile name already exists.' };
        }
        return {};
    }
}
